import { getInvoicesList, getPaymentsList } from 'actions/billing'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import AddIcon from 'components/ReusableComponents/Icons/Add'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import billingApi from 'services/api/billing'
import { delay } from 'utils/delay'

import styles from './AddCustomChargeDialog.module.scss'
import { customInvoiceCategoryOptions } from './helpers/getCategoryOptions'
import { useChargesState } from './helpers/useChargesState'
import { validateCharges } from './helpers/validateCharges'
import SubmitConfirmationDialog from './SubmitConfirmationDialog'

const AddCustomChargeDialog = ({
  onClose,
  isForChargeback,
  paymentId,
  paymentAmount
}) => {
  const { addToast } = useToasts()
  const dispatch = useDispatch()

  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const initialChargeKind = isForChargeback ? 'Chargeback' : ''
  const initialDescription = isForChargeback ? `Chargeback for payment #${paymentId}` : ''
  const initialAmount = paymentAmount ? String(paymentAmount) : ''
  const categoryOptions = customInvoiceCategoryOptions(isForChargeback)

  const showConfirmationDialogInitially = paymentAmount && paymentId

  const { charges, handleChargeChange, handleAddCharge, handleRemoveCharge } =
    useChargesState({
      initialChargeKind,
      initialDescription,
      initialAmount
    })

  const handleConfirm = () => {
    const isInvalid = validateCharges(charges, addToast)

    if (!isInvalid) {
      setShowSubmitConfirmation(true)
    }
  }

  const handleFinalSubmit = () => {
    setIsSubmitting(true)
    const chargesToSubmit = charges.map(({ id, ...rest }) => ({
      ...rest,
      amount: Number(rest.amount)
    }))
    const data = {
      charges: chargesToSubmit
    }

    billingApi
      .addCustomCharge(data)
      .then(() => {
        addToast('Custom charges added successfully!', { appearance: 'success' })
      })
      .then(() => {
        if (isForChargeback && paymentId) {
          return billingApi.setPaymentAsChargeback({ paymentId })
        }
      })
      .then(() => {
        //TODO: This is a temporary fix to avoid race condition.
        //We need to find a better solution to avoid this on the backend.
        delay(1000).then(() => {
          dispatch(getInvoicesList())
          dispatch(getPaymentsList())
        })
      })
      .catch(error => {
        addToast(`${error.message.toString()}`, {
          appearance: 'error',
          autoDismiss: true
        })
        dispatch(getInvoicesList())
      })
      .finally(() => {
        onClose()
        setShowSubmitConfirmation(false)
        setIsSubmitting(false)
      })
  }

  const totalAmount = charges.reduce((sum, charge) => {
    const amount = parseFloat(charge.amount)
    return sum + (isNaN(amount) ? 0 : amount)
  }, 0)

  return (
    <>
      <ConfirmationDialog
        title="Create custom charge invoice"
        theme="blacklist"
        positiveMessage="Add"
        negativeMessage="Cancel"
        onClose={onClose}
        onConfirm={handleConfirm}
      >
        <div className={styles.formContainer}>
          {charges.map(charge => {
            return (
              <div key={charge.id} className={styles.chargeItem}>
                <>
                  <div className={styles.chargeFields}>
                    <Dropdown
                      label="Category"
                      options={categoryOptions}
                      value={charge.chargeKind}
                      name="chargeKind"
                      onChange={e => handleChargeChange(charge.id, e)}
                      defaultVal="Select category"
                      className={styles.inputField}
                    />

                    <TextInput
                      label="Charge description"
                      value={charge.description}
                      name="description"
                      onChange={e => handleChargeChange(charge.id, e)}
                      placeholder="Enter description"
                      className={styles.inputField}
                    />

                    <TextInput
                      label="Custom charge amount"
                      value={charge.amount}
                      name="amount"
                      onChange={e => handleChargeChange(charge.id, e)}
                      placeholder="0.00"
                      type="number"
                      className={styles.inputField}
                    />
                  </div>
                  <div className={styles.removeButtonContainer}>
                    {charges.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveCharge(charge.id)}
                        className={styles.removeButton}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </>
              </div>
            )
          })}
          <div onClick={handleAddCharge} className={styles.addButton}>
            <AddIcon fill="#4079F5" height={24} width={24} />
            Add another charge
          </div>
        </div>
      </ConfirmationDialog>

      {(showSubmitConfirmation || showConfirmationDialogInitially) && (
        <SubmitConfirmationDialog
          onClose={() =>
            showConfirmationDialogInitially ? onClose() : setShowSubmitConfirmation(false)
          }
          onConfirm={handleFinalSubmit}
          totalAmount={totalAmount}
          chargeCount={charges.length}
          isLoading={isSubmitting}
          charges={charges}
        />
      )}
    </>
  )
}

export default AddCustomChargeDialog
